import React, { createContext, useState, useEffect, useContext } from 'react';
import commonApi from '../utils/api';
import { CommonContext } from './CommonContext';
import { format } from 'date-fns';
import { useLocation, useNavigate } from 'react-router-dom';
import moment from 'moment';

export const WarehouseContext = createContext();

const initialFilters = {
    searchBy: 'contact_person',
    searchValue: '',
    status: '',
    dateRange: {
        startDate: null,
        endDate: null,
    },
};

export function WarehouseContextProvider({ children }) {
    const { user, setModalAlert, setOpenAlert, setAlertMessage, setSeverity } = useContext(CommonContext);

    const [addModal, setAddModal] = useState(false);
    const [allData, setAllData] = useState([]);
    const [allWarehouse, setAllWarehouse] = useState([]);
    const [viewData, setViewData] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(50);
    const [isLoading, setIsLoading] = useState(false);
    const [firstLoading, setFirstLoading] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [selected, setSelected] = useState([]);
    const [filters, setFilters] = useState(initialFilters);
    const [locationState, setLocationState] = useState(null);

    const location = useLocation();
    const navigate = useNavigate();

    const handleAddModal = () => {
        setAddModal(true);
        setModalAlert(false);
        setOpenAlert(false);
        setAlertMessage('');
        setSeverity('');
    };

    const handleCloseModal = () => {
        setViewData('')
        setIsEdit(false);
        setAddModal(false);
    };

    const onLoadApi = (customFilters = filters) => {
        let payload = {
            ...customFilters,
            start_date: customFilters?.dateRange?.startDate
                ? format(new Date(customFilters.dateRange.startDate), 'yyyy-MM-dd')
                : null,
            end_date: customFilters?.dateRange?.endDate
                ? format(new Date(customFilters.dateRange.endDate), 'yyyy-MM-dd')
                : null,
        };

        if (user?.user_type === "Merchant") {
            payload.merchant_id = user?.id;
        } else if (location?.state) {
            payload.merchant_id = location.state.id;
            setLocationState(location.state);
        } else {
            payload.admin_id = user?.id;
        }

        delete payload.dateRange;
        setAllWarehouse([]);
        setAllData([]);
        setFirstLoading(false);
        commonApi.post(`/merchant-warehouse/list?page=${page + 1}&per_page=${rowsPerPage}`, payload)
            .then((response) => {
                setFirstLoading(true);
                if (response?.data?.data) {
                    setAllWarehouse(response?.data?.data);
                    setAllData(response?.data);
                }
            })
            .catch((error) => {
                setFirstLoading(true);
                console.error('Error fetching data:', error);
            });
    };

    const viewDetail = (id) => {
        setIsLoading(true);
        commonApi.post('/merchant-warehouse/view', { id: id })
            .then((response) => {
                setIsLoading(false);
                if (response?.data?.data) {
                    setIsEdit(true);
                    setViewData(response?.data?.data);
                    handleAddModal();
                }
            })
            .catch((error) => {
                setIsLoading(false);
                console.error('Error fetching data:', error);
            });
    }

    useEffect(() => {
        onLoadApi();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, rowsPerPage]);

    const onExport = () => {
        let payload = {
            ...filters,
            start_date: filters?.dateRange?.startDate
                ? format(new Date(filters.dateRange.startDate), 'yyyy-MM-dd')
                : null,
            end_date: filters?.dateRange?.endDate
                ? format(new Date(filters.dateRange.endDate), 'yyyy-MM-dd')
                : null,
        };

        if (user?.user_type === "Merchant") {
            payload.merchant_id = user?.id;
        } else if (location?.state) {
            payload.merchant_id = location.state.id;
        } else {
            payload.admin_id = user?.id;
        }

        if (selected.length > 0) {
            payload.ids = selected.join(', ');
        }

        delete payload.dateRange;
        setIsLoading(true);
        commonApi.post('/merchant-warehouse/export', payload)
            .then((response) => {
                setIsLoading(false);
                setOpenAlert(true);
                if (response?.data?.s === 0) {
                    setAlertMessage(response?.data?.msg);
                    setSeverity('error');
                } else {
                    setAlertMessage(response?.data?.msg || 'Export started successfully');
                    setSeverity('success');
                    navigate('/download');
                }
            })
            .catch((error) => {
                setIsLoading(false);
                console.error('Error exporting data:', error);
                setOpenAlert(true);
                setAlertMessage(error?.response?.data?.msg || 'Something went wrong');
                setSeverity('error');
            });
    };

    // You can provide any values or functions you want to make available to consumers here
    const contextValue = {
        locationState,
        setLocationState,
        filters,
        setFilters,
        initialFilters,
        viewDetail,
        page,
        setPage,
        rowsPerPage,
        setRowsPerPage,
        selected,
        setSelected,
        isEdit,
        onLoadApi,
        isLoading,
        firstLoading,
        viewData,
        allWarehouse,
        allData,
        handleAddModal,
        handleCloseModal,
        setAddModal,
        addModal,
        setAllData,
        setAllWarehouse,
        setViewData,
        setFirstLoading,
        setIsLoading,
        setIsEdit,
        onExport,
    };

    // Render the provider with children
    return <WarehouseContext.Provider value={contextValue}>{children}</WarehouseContext.Provider>;
}
